import React from 'react';

import {Box, CircularProgress, Paper, Stack, Typography} from 'views/components';
import {Translate, translate, useNavigate} from 'views/module';

import backend from 'utils/backend';

const SituationSource = () => {
  const navigate = useNavigate();
  const [error, setError] = React.useState(null);
  const [chat, setChat] = React.useState(null);
  const targetMessageRef = React.useRef(null);

  React.useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const clientRn = searchParams.get('clientRn');
    const source = searchParams.get('source');
    const sourceRn = searchParams.get('sourceRn');

    const resolveSource = async () => {
      try {
        if (!clientRn || !sourceRn) {
          throw new Error(translate('client.ClientDetail.Situation.SourceResolver.IncompleteLink'));
        }

        if (source === 'CLIENT_SUPPORT_EVENT_FEEDBACK') {
          const feedback = await backend.clientSupport.getEventFeedback(sourceRn);
          navigate(
              `/schedule/event/${encodeURIComponent(feedback.eventRn)}?feedbackRn=${encodeURIComponent(sourceRn)}#shift-feedback`,
              {replace: true},
          );
          return;
        }

        if (source === 'CLIENT_MESSAGE') {
          navigate(
              `/client/${encodeURIComponent(clientRn)}?feedTab=CORRESPONDANCE&messageRn=${encodeURIComponent(sourceRn)}`,
              {replace: true},
          );
          return;
        }

        if (source === 'COMMUNICATION_CONVERSATION_MESSAGES') {
          const messageMarkerIndex = sourceRn.lastIndexOf(':MESSAGE-V1-');
          if (messageMarkerIndex < 0) {
            throw new Error(translate('client.ClientDetail.Situation.SourceResolver.UnknownConversation'));
          }

          const conversationRn = sourceRn.slice(0, messageMarkerIndex);
          let conversation = null;
          try {
            const output = await backend.communication.getConversationList(clientRn);
            conversation = (output?.list || []).find((item) => item.conversationRn === conversationRn);
          } catch {
            // Some family chats are attached to a client relationship rather than the root client RN.
          }
          const caregiver = conversation?.participants?.find((participant) => {
            return /^RN:CAREGIVER-V1-/.test(participant.userRn);
          });

          if (caregiver) {
            navigate(
                `/caregiver/${encodeURIComponent(caregiver.userRn)}` +
                `?feedTab=CHATS&conversationRn=${encodeURIComponent(conversationRn)}` +
                `&messageRn=${encodeURIComponent(sourceRn)}`,
                {replace: true},
            );
            return;
          }

          const messageOutput = await backend.communication.getConversationMessages(clientRn, conversationRn);
          setChat({
            conversationRn,
            sourceRn,
            messages: messageOutput?.list || [],
          });
          return;
        }

        throw new Error(translate('client.ClientDetail.Situation.SourceResolver.UnlinkableSource'));
      } catch (sourceError) {
        setError(sourceError.message || translate('client.ClientDetail.Situation.SourceResolver.OpenFailed'));
      }
    };

    resolveSource();
  }, [navigate]);

  React.useEffect(() => {
    if (!chat) {
      return;
    }

    targetMessageRef.current?.scrollIntoView({behavior: 'smooth', block: 'center'});
  }, [chat]);

  if (chat) {
    const messages = [...chat.messages].sort((first, second) => {
      return new Date(first.createdAt || 0) - new Date(second.createdAt || 0);
    });

    return (
      <Box sx={{maxWidth: 800, mx: 'auto', p: 3}}>
        <Typography variant='h5' sx={{mb: 2}}>
          <Translate expression='client.ClientDetail.Situation.SourceResolver.ChatTitle' />
        </Typography>
        <Stack spacing={1.5}>
          {
            messages.map((message) => {
              const isTarget = message.messageRn === chat.sourceRn;
              const requesterName = [message.requester?.firstName, message.requester?.lastName]
                  .filter(Boolean).join(' ');

              return (
                <Paper
                  key={message.messageRn || `${message.requesterRn}-${message.createdAt}`}
                  ref={isTarget ? targetMessageRef : undefined}
                  sx={{
                    p: 2,
                    border: isTarget ? '3px solid' : '1px solid',
                    borderColor: isTarget ? 'warning.main' : 'divider',
                  }}
                >
                  <Typography sx={{fontSize: '12px', fontWeight: 700, color: 'text.secondary'}}>
                    {requesterName || message.requesterRn}
                  </Typography>
                  <Typography sx={{whiteSpace: 'pre-wrap'}}>{message.messageText}</Typography>
                </Paper>
              );
            })
          }
        </Stack>
      </Box>
    );
  }

  return (
    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh'}}>
      {
        error ?
          <Typography color='error'>{error}</Typography> :
          <CircularProgress />
      }
    </Box>
  );
};

export default SituationSource;
